import { localizarPontoNoMundo } from './ancoragem'
import { mundoParaLocalDoObjeto } from './ancoragem'
import type { CenaTabletop, CordaTabletop, ObjetoTabletop, ReferenciaPonto, VetorTabletop } from './tipos'

/**
 * A polia se acomoda na corda: pendurada pelo olhal, ela gira até a roldana
 * ficar no plano das duas pernas e o olhal apontar para o lado oposto a elas
 * (para a ancoragem que a segura). Assim a corda corre no canal, e não pela
 * placa lateral. Também: para que lado cada roldana gira quando a corda anda.
 * TypeScript puro.
 */

type V = VetorTabletop

/** Os pares entrada/saída de cada roldana das polias. */
const PARES: Readonly<Partial<Record<ObjetoTabletop['tipo'], ReadonlyArray<readonly [string, string]>>>> = {
  polia: [['entrada', 'saida']],
  poliaDupla: [
    ['entrada-1', 'saida-1'],
    ['entrada-2', 'saida-2'],
  ],
}

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const soma = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const esc = (a: V, k: number): V => [a[0] * k, a[1] * k, a[2] * k]
const prod = (a: V, b: V) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const cruz = (a: V, b: V): V => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
const unit = (a: V): V | null => {
  const n = Math.hypot(a[0], a[1], a[2])
  return n > 1e-9 ? esc(a, 1 / n) : null
}

/** Uma passagem da corda por uma roldana: os pontos antes da entrada e depois da saída. */
interface Passagem {
  readonly corda: CordaTabletop
  /** Índice da entrada (ou saída, a que vem primeiro no percurso). */
  readonly indice: number
  readonly antes: ReferenciaPonto
  readonly depois: ReferenciaPonto
}

function passagensPelaPolia(cena: CenaTabletop, objeto: ObjetoTabletop): Passagem[] {
  const pares = PARES[objeto.tipo]
  if (!pares) return []
  const achadas: Passagem[] = []
  for (const corda of cena.cordas ?? []) {
    const p = corda.percurso
    for (let i = 1; i + 2 < p.length; i += 1) {
      const a = p[i]!
      const b = p[i + 1]!
      if (a.idObjeto !== objeto.id || b.idObjeto !== objeto.id) continue
      if (pares.some(([e, s]) => (a.idPonto === e && b.idPonto === s) || (a.idPonto === s && b.idPonto === e))) {
        achadas.push({ corda, indice: i, antes: p[i - 1]!, depois: p[i + 2]! })
      }
    }
    // a corda marcada só na entrada (ou só na saída) também passa pela roldana: vem de um lado e segue para o outro
    for (let i = 1; i + 1 < p.length; i += 1) {
      const r = p[i]!
      if (r.idObjeto !== objeto.id || p[i - 1]!.idObjeto === objeto.id || p[i + 1]!.idObjeto === objeto.id) continue
      if (pares.some(([e, s]) => r.idPonto === e || r.idPonto === s)) achadas.push({ corda, indice: i, antes: p[i - 1]!, depois: p[i + 1]! })
    }
  }
  return achadas
}

/** Euler (ordem YXZ, a mesma da cena) da rotação cujas colunas são os eixos locais X, Y e Z. */
function eulerYXZ(x: V, y: V, z: V): [number, number, number] {
  const m23 = z[1]
  const rx = Math.asin(-Math.min(1, Math.max(-1, m23)))
  if (Math.abs(m23) < 0.9999999) return [rx, Math.atan2(z[0], z[2]), Math.atan2(x[1], y[1])]
  return [rx, Math.atan2(-x[2], x[0]), 0]
}

/**
 * A rotação que acomoda a polia na primeira corda que passa por ela, ou
 * `null` se nenhuma corda passa pela roldana (com um ponto antes e um depois)
 * ou se as pernas estão em linha reta (não há plano).
 */
export function rotacaoDaPoliaNaCorda(cena: CenaTabletop, objeto: ObjetoTabletop): [number, number, number] | null {
  const [passagem] = passagensPelaPolia(cena, objeto)
  if (!passagem) return null
  const centro = objeto.transformacao.posicao as V
  const a = localizarPontoNoMundo(cena, passagem.antes)
  const b = localizarPontoNoMundo(cena, passagem.depois)
  if (!a || !b) return null
  const ua = unit(sub(a, centro))
  const ub = unit(sub(b, centro))
  if (!ua || !ub) return null
  // o olhal aponta contra a resultante das pernas
  const y = unit(esc(soma(ua, ub), -1))
  if (!y) return null
  let z = unit(cruz(ua, ub)) ?? unit(cruz(y, Math.abs(y[0]) < 0.9 ? [1, 0, 0] : [0, 0, 1]))!
  let x = cruz(y, z)
  // a entrada (−X local) fica do lado de onde a corda vem
  const entradaPrimeiro = passagem.corda.percurso[passagem.indice]!.idPonto.startsWith('entrada')
  const ladoDaEntrada = entradaPrimeiro ? ua : ub
  if (prod(ladoDaEntrada, x) > 0) {
    z = esc(z, -1)
    x = esc(x, -1)
  }
  return eulerYXZ(x, y, z)
}

/** Acomoda a polia na corda (ver `rotacaoDaPoliaNaCorda`); sem corda que passe por ela, a cena não muda. */
export function alinharPoliaComCorda(cena: CenaTabletop, idObjeto: string): CenaTabletop {
  const objeto = cena.objetos.find((o) => o.id === idObjeto)
  const rotacao = objeto ? rotacaoDaPoliaNaCorda(cena, objeto) : null
  if (!objeto || !rotacao) return cena
  return {
    ...cena,
    objetos: cena.objetos.map((o) => (o.id === idObjeto ? { ...o, transformacao: { ...o.transformacao, rotacao } } : o)),
  }
}

/** Acomoda todas as polias por cujas roldanas a corda passa. */
export function alinharPoliasDaCorda(cena: CenaTabletop, idCorda: string): CenaTabletop {
  const corda = cena.cordas?.find((c) => c.id === idCorda)
  if (!corda) return cena
  const ids = new Set(
    cena.objetos.filter((o) => PARES[o.tipo] && passagensPelaPolia({ ...cena, cordas: [corda] }, o).length > 0).map((o) => o.id),
  )
  return [...ids].reduce((c, id) => alinharPoliaComCorda(c, id), cena)
}

/**
 * Acomoda na corda todas as polias por cujas roldanas passa alguma corda:
 * pendurada, a polia gira sozinha até a roldana ficar no plano da corda. Vale
 * para as cenas salvas antes de a polia se acomodar ao desenhar a corda.
 */
export function acomodarPoliasNasCordas(cena: CenaTabletop): CenaTabletop {
  return cena.objetos.filter((o) => PARES[o.tipo] && temCordaNaRodana(cena, o)).reduce((c, o) => alinharPoliaComCorda(c, o.id), cena)
}

/** Polias pelas quais alguma corda passa: as que aceitam o botão de alinhar. */
export const temCordaNaRodana = (cena: CenaTabletop, objeto: ObjetoTabletop) => passagensPelaPolia(cena, objeto).length > 0

/**
 * Para que lado cada roldana gira quando a corda anda do início para o fim do
 * percurso: +1 no sentido anti-horário em volta do eixo Z da polia, −1 no
 * horário. A corda arrasta a roldana pelo lado do contato, que é o oposto às pernas.
 */
export function giroDasRoldanasNaCorda(
  cena: CenaTabletop,
  corda: CordaTabletop,
): ReadonlyArray<{ readonly idObjeto: string; readonly roldana: number; readonly sentido: 1 | -1 }> {
  const resultado: Array<{ idObjeto: string; roldana: number; sentido: 1 | -1 }> = []
  for (const objeto of cena.objetos) {
    const pares = PARES[objeto.tipo]
    if (!pares) continue
    for (const p of passagensPelaPolia({ ...cena, cordas: [corda] }, objeto)) {
      const a = localizarPontoNoMundo(cena, p.antes)
      const b = localizarPontoNoMundo(cena, p.depois)
      if (!a || !b) continue
      const la = mundoParaLocalDoObjeto(a, objeto.transformacao)
      const lb = mundoParaLocalDoObjeto(b, objeto.transformacao)
      // o contato fica do lado oposto às pernas; a corda vai de A para B passando por ele
      const meio: V = [-(la[0] + lb[0]), -(la[1] + lb[1]), 0]
      const giro = cruz([la[0], la[1], 0], meio)[2] + cruz(meio, [lb[0], lb[1], 0])[2]
      const roldana = pares.findIndex(([e, s]) => [e, s].includes(corda.percurso[p.indice]!.idPonto))
      resultado.push({ idObjeto: objeto.id, roldana: Math.max(0, roldana), sentido: giro >= 0 ? 1 : -1 })
    }
  }
  return resultado
}
